import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { UserContext } from '@/components/App';
import {
    REINVITE_CARRIER,
    REVIEW_CONTRACTOR_COMPLIANCE,
    SET_CARRIER_INTEGRATIONS_REQUIRED,
    SET_SHIPPER_INTEGRATIONS_REQUIRED,
} from '@/constants/apiRoutes';
import { useClientUser } from '@/hooks';
import { post } from '@/utilities/onwardClient';

import { GET_ORG_CONTRACTOR_PARTNERSHIPS } from '../graphql';
import { CorpAdminContext } from '../store';
import { BADGES, BUCKETS, SHIPPER_BADGES, SHIPPER_BUCKETS } from './constants';
import { GET_ORG_CARRIER_ONBOARDING, GET_ORG_SHIPPER_ONBOARDING } from './graphql';
import { bucketCounts, filterByFacets, filterRows, NO_FILTERS, rowsInBucket } from './utils';

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;

// menuOptionsFor key -> the read-only dialog it opens.
const MENU_OPTION_MODES = { VIEW_DECISION: 'decision', VIEW_HISTORY: 'history' };

/**
 * Approve / Deny stepping through the selected contractors, one notes dialog each.
 *
 * @typedef {object} ReviewQueue
 * @property {'APPROVED'|'DENIED'} decision The decision every contractor in the queue gets.
 * @property {object[]} rows The selected organization_carrier_statuses rows, in dialog order.
 * @property {number} index The row the dialog is on.
 */

/**
 * The read-only review dialog a card's hamburger menu opened.
 *
 * @typedef {object} Viewing
 * @property {'decision'|'history'} mode 'decision' is View Approval / View Rejection; 'history' is View Compliance History.
 * @property {object} row The card's organization_carrier_statuses row.
 */

/**
 * What both onboarding views share: the org's rows from `query`, the active tab, the type / badge filter and the
 * checked rows. `idField` is the row's account ID column (`carrier_id` or `shipper_id`).
 *
 * @param {{ query: object, rowsField: string, idField: string, buckets: object[], badges: object[], search: string }}
 *   args
 */
const useOnboardingRows = ({ query, rowsField, idField, buckets, badges, search }) => {
    const { organization_id } = useClientUser();

    const { data, loading, error, refetch } = useQuery(query, {
        variables: { organization_id },
        skip: !organization_id,
        fetchPolicy: 'cache-and-network',
    });

    const [bucketKey, setBucketKey] = useState(buckets[0].key);
    // The type / badge filter. It applies to every tab and their counts, and stays put when the tab changes.
    const [filters, setFilters] = useState(NO_FILTERS);
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    const bucket = buckets.find((b) => b.key === bucketKey) || buckets[0];
    const rows = useMemo(() => {
        const searchedRows = filterRows(data?.[rowsField], search);
        return filterByFacets(searchedRows, filters, badges);
    }, [data, rowsField, search, filters, badges]);
    const counts = useMemo(() => bucketCounts(rows, buckets), [rows, buckets]);
    const bucketRows = useMemo(() => rowsInBucket(rows, bucket), [rows, bucket]);
    const selectedRows = useMemo(
        () => bucketRows.filter((row) => selectedIds.has(row[idField])),
        [bucketRows, idField, selectedIds]
    );

    // A checked row stays checked only while it's on screen.
    useEffect(() => setSelectedIds(new Set()), [bucketKey, search, filters]);

    const toggleRow = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    return {
        organization_id,
        refetch,
        loading: loading && !data,
        error,
        hasOrganization: !!organization_id,
        bucket,
        setBucketKey,
        filters,
        setFilters,
        counts,
        bucketRows,
        selectedIds,
        selectedRows,
        toggleRow,
        clearSelection,
    };
};

/**
 * Sets or clears No Integrations Required on each of the checked rows through `route`, reports how it went, then
 * clears the selection and re-reads the view. The account ID goes in the body under `idField`.
 *
 * @returns {(targets: object[], integrationsRequired: boolean) => Promise<void>}
 */
const useSetIntegrationsRequired = ({ route, idField, organization_id, notify, setBusy, clearSelection, refetch }) =>
    useCallback(
        async (targets, integrationsRequired) => {
            if (!targets?.length) return;
            setBusy(true);
            // organization_id is only read by the emulator; deployed environments take it from the token.
            const results = await Promise.allSettled(
                targets.map((row) =>
                    post(route, {
                        [idField]: row[idField],
                        organization_id,
                        integrations_required: integrationsRequired,
                    })
                )
            );
            setBusy(false);

            const failed = results.filter((result) => result.status === 'rejected');
            failed.forEach((result) => Sentry.captureException(result.reason));
            if (failed.length) {
                notify?.({
                    severity: 'error',
                    message:
                        targets.length === 1
                            ? errorMessage(failed[0].reason, 'Error saving the integrations setting')
                            : `${failed.length} of ${targets.length} integrations settings failed`,
                });
            } else {
                notify?.({
                    severity: 'success',
                    message: integrationsRequired ? 'Integrations required' : 'Marked No Integrations Required',
                });
            }
            clearSelection();
            refetch();
        },
        [clearSelection, idField, notify, organization_id, refetch, route, setBusy]
    );

/**
 * Everything the carrier onboarding view renders and does: the org's rows from organization_carrier_statuses, the
 * active tab, the checked rows, the review queue and open dialogs, and the writes behind them. Writes go through the
 * onward-functions corp endpoints, then re-read the view.
 *
 * @param {{ search: string }} args
 */
export const useCarrierOnboarding = ({ search }) => {
    const { user } = useContext(UserContext);
    const { callbacks } = useContext(CorpAdminContext);
    const notify = callbacks?.setNotification;
    const apolloClient = useApolloClient();

    const { organization_id, refetch, ...view } = useOnboardingRows({
        query: GET_ORG_CARRIER_ONBOARDING,
        rowsField: 'organization_carrier_statuses',
        idField: 'carrier_id',
        buckets: BUCKETS,
        badges: BADGES,
        search,
    });
    const { clearSelection } = view;

    const [reviewQueue, setReviewQueue] = useState(/** @type {ReviewQueue|null} */ (null));
    const [viewing, setViewing] = useState(/** @type {Viewing|null} */ (null));
    const [busy, setBusy] = useState(false);

    const reportError = useCallback(
        (err, fallback) => {
            Sentry.captureException(err);
            notify?.({ severity: 'error', message: errorMessage(err, fallback) });
        },
        [notify]
    );

    // `skipped` is how many checked carriers were left out: signed up already, no email, or a Flex Center.
    const reinvite = useCallback(
        async (targets, skipped = 0) => {
            if (!targets?.length) return;
            setBusy(true);
            const inviterName = user?.business_name || user?.username || undefined;
            // organization_id is only read by the emulator; deployed environments take it from the token.
            const results = await Promise.allSettled(
                targets.map((row) =>
                    post(REINVITE_CARRIER, {
                        carrier_id: row.carrier_id,
                        organization_id,
                        inviter_name: inviterName,
                    })
                )
            );
            setBusy(false);

            const failed = results.filter((result) => result.status === 'rejected');
            failed.forEach((result) => Sentry.captureException(result.reason));
            const skippedNote = skipped ? `. ${skipped} skipped (already signed up, no email, or a Flex Center)` : '';
            if (failed.length) {
                notify?.({
                    severity: 'error',
                    message:
                        (targets.length === 1
                            ? errorMessage(failed[0].reason, 'Error sending the invite')
                            : `${failed.length} of ${targets.length} invites failed`) + skippedNote,
                });
            } else {
                notify?.({
                    severity: 'success',
                    message: (targets.length === 1 ? 'Invite sent' : `${targets.length} invites sent`) + skippedNote,
                });
            }
            clearSelection();
            refetch();
        },
        [clearSelection, notify, organization_id, refetch, user]
    );

    const startReview = useCallback((decision, targets) => {
        if (targets?.length) setReviewQueue({ decision, rows: targets, index: 0 });
    }, []);

    const advanceReview = useCallback(() => {
        setReviewQueue((queue) => {
            if (!queue || queue.index + 1 >= queue.rows.length) return null;
            return { ...queue, index: queue.index + 1 };
        });
    }, []);

    // A review changes the contractor's partnership status, and the List View's Motor Carriers (store.js) list
    // APPROVED ones only, so both views re-read.
    const refetchAfterReview = useCallback(() => {
        refetch();
        apolloClient.refetchQueries({ include: [GET_ORG_CONTRACTOR_PARTNERSHIPS] }).catch(Sentry.captureException);
    }, [apolloClient, refetch]);

    const finishReviewIfLast = useCallback(() => {
        if (reviewQueue && reviewQueue.index + 1 >= reviewQueue.rows.length) {
            clearSelection();
            refetchAfterReview();
        }
    }, [clearSelection, refetchAfterReview, reviewQueue]);

    /** Save the current contractor's decision with `note`, then move to the next one. */
    const submitReview = useCallback(
        async (note) => {
            const row = reviewQueue?.rows[reviewQueue.index];
            if (!row) return;
            setBusy(true);
            try {
                await post(REVIEW_CONTRACTOR_COMPLIANCE, {
                    carrier_id: row.carrier_id,
                    organization_id,
                    status: reviewQueue.decision,
                    note: note || null,
                });
                finishReviewIfLast();
                advanceReview();
            } catch (err) {
                reportError(err, `Error saving the review for ${row.carrier?.business_name || 'this Motor Carrier'}`);
            } finally {
                setBusy(false);
            }
        },
        [advanceReview, finishReviewIfLast, organization_id, reportError, reviewQueue]
    );

    /** Leave the current contractor as it is and move to the next one. */
    const skipReview = useCallback(() => {
        finishReviewIfLast();
        advanceReview();
    }, [advanceReview, finishReviewIfLast]);

    // Cancel can come after some decisions were saved.
    const cancelReview = useCallback(() => {
        setReviewQueue(null);
        refetchAfterReview();
    }, [refetchAfterReview]);

    const setIntegrationsRequired = useSetIntegrationsRequired({
        route: SET_CARRIER_INTEGRATIONS_REQUIRED,
        idField: 'carrier_id',
        organization_id,
        notify,
        setBusy,
        clearSelection,
        refetch,
    });

    /** Run a card's hamburger-menu option (`menuOptionsFor` keys). */
    const runMenuOption = useCallback((row, key) => {
        const mode = MENU_OPTION_MODES[key];
        if (!mode) throw new Error(`Unknown onboarding menu option: ${key}`);
        setViewing({ mode, row });
    }, []);

    return {
        ...view,
        busy,
        reinvite,
        setIntegrationsRequired,
        startReview,
        reviewQueue,
        submitReview,
        skipReview,
        cancelReview,
        viewing,
        closeViewing: () => setViewing(null),
        runMenuOption,
    };
};

/**
 * Everything the shipper onboarding view renders and does: the org's rows from organization_shipper_statuses, the
 * active tab, the checked rows, and the No Integrations Required toggle behind the options panel.
 *
 * @param {{ search: string }} args
 */
export const useShipperOnboarding = ({ search }) => {
    const { callbacks } = useContext(CorpAdminContext);
    const notify = callbacks?.setNotification;

    const { organization_id, refetch, ...view } = useOnboardingRows({
        query: GET_ORG_SHIPPER_ONBOARDING,
        rowsField: 'organization_shipper_statuses',
        idField: 'shipper_id',
        buckets: SHIPPER_BUCKETS,
        badges: SHIPPER_BADGES,
        search,
    });
    const [busy, setBusy] = useState(false);

    const setIntegrationsRequired = useSetIntegrationsRequired({
        route: SET_SHIPPER_INTEGRATIONS_REQUIRED,
        idField: 'shipper_id',
        organization_id,
        notify,
        setBusy,
        clearSelection: view.clearSelection,
        refetch,
    });

    return { ...view, busy, setIntegrationsRequired };
};
